import { useId, useMemo, useState } from 'react';

import { Collapse, Typography as Text } from '@material-ui/core';

import { RiArrowRightSLine } from '@remixicon/react';
import classNames from 'classnames';

import { DisplayItem } from '../../hooks/groupChatEvents';
import { LoadingIndicator } from '../LoadingIndicator';
import { MarkdownContent } from '../MarkdownContent';
import { ToolCallGroup } from '../ToolCallGroup';

import styles from './ThoughtProcess.module.css';

type ThoughtProcessProps = {
  items: DisplayItem[];
  isActive: boolean;
  isInterrupted: boolean;
  isLatest: boolean;
  // Whether the completed thought process starts expanded. Defaults to collapsed
  // for the main thread (where it is a low-priority recap); surfaces that make
  // the reasoning the primary content (e.g. a coding session transcript) pass true.
  defaultExpanded?: boolean;
};

const getStepContent = (item: DisplayItem): string | null => {
  if (item.type === 'tool-group') return null;
  if (item.event.thinking) return item.event.thinking.content;
  if (item.event.assistantMessage) return item.event.assistantMessage.content;
  return null;
};

const isVisibleInActiveMode = (item: DisplayItem): boolean =>
  item.type === 'tool-group' || Boolean(getStepContent(item));

const COLLAPSE_THRESHOLD = 3;

const isToolGroup = (
  item: DisplayItem,
): item is DisplayItem & { type: 'tool-group' } => item.type === 'tool-group';

type AllStepsProps = {
  visibleItems: DisplayItem[];
  lastCommentaryIdx: number;
  isInterrupted: boolean;
  lastStepStatus?: 'active' | 'completed';
};

const AllSteps = ({
  visibleItems,
  lastCommentaryIdx,
  isInterrupted,
  lastStepStatus = 'active',
}: AllStepsProps) => (
  <>
    {visibleItems.map((item, idx) => {
      if (item.type === 'tool-group') {
        return (
          <ToolCallGroup
            key={item.key}
            entries={item.entries}
            variant="inline"
          />
        );
      }

      const content = getStepContent(item);
      if (!content) return null;

      const isLastStep = idx === lastCommentaryIdx;
      const isActive =
        isLastStep && !isInterrupted && lastStepStatus === 'active';

      return (
        <div
          key={item.key}
          className={
            isActive ? styles.commentaryActive : styles.commentaryCompleted
          }
        >
          <MarkdownContent>{content}</MarkdownContent>
        </div>
      );
    })}
  </>
);

const StepsToggle = ({
  expanded,
  stepCount,
  onClick,
}: {
  expanded: boolean;
  stepCount: number;
  onClick: () => void;
}) => (
  <button
    type="button"
    className={classNames(styles.header, styles.stepsToggle)}
    aria-expanded={expanded}
    onClick={onClick}
  >
    <RiArrowRightSLine
      size={14}
      className={classNames(styles.arrow, {
        [styles.arrowExpanded]: expanded,
      })}
    />
    <Text variant="body2" color="textSecondary">
      {stepCount} steps
    </Text>
  </button>
);

const ActiveThoughtProcess = ({
  items,
  isInterrupted,
}: {
  items: DisplayItem[];
  isInterrupted: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  const visibleItems = useMemo(
    () => items.filter(isVisibleInActiveMode),
    [items],
  );

  const lastCommentaryIdx = useMemo(() => {
    for (let i = visibleItems.length - 1; i >= 0; i--) {
      if (getStepContent(visibleItems[i])) return i;
    }
    return -1;
  }, [visibleItems]);

  if (visibleItems.length === 0) {
    return <LoadingIndicator />;
  }

  const isCollapsible = visibleItems.length >= COLLAPSE_THRESHOLD;
  const isCollapsed = isCollapsible && !expanded;

  if (isCollapsed) {
    const latestThinking = visibleItems.findLast(item =>
      Boolean(getStepContent(item)),
    );
    const latestToolGroup = visibleItems.findLast(isToolGroup);

    return (
      <div className={styles.activeRoot}>
        <StepsToggle
          expanded={false}
          stepCount={visibleItems.length}
          onClick={() => setExpanded(true)}
        />
        {latestThinking && (
          <div
            key={latestThinking.key}
            className={
              isInterrupted
                ? styles.commentaryCompleted
                : styles.commentaryActive
            }
          >
            <MarkdownContent>
              {getStepContent(latestThinking) ?? ''}
            </MarkdownContent>
          </div>
        )}
        {latestToolGroup && (
          <ToolCallGroup
            key={latestToolGroup.key}
            entries={latestToolGroup.entries}
            variant="inline"
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.activeRoot}>
      {isCollapsible && (
        <StepsToggle
          expanded
          stepCount={visibleItems.length}
          onClick={() => setExpanded(false)}
        />
      )}
      <AllSteps
        visibleItems={visibleItems}
        lastCommentaryIdx={lastCommentaryIdx}
        isInterrupted={isInterrupted}
      />
    </div>
  );
};

const ThoughtProcessContent = ({ items }: { items: DisplayItem[] }) => (
  <div className={styles.content}>
    {items.map(item => {
      if (item.type === 'tool-group') {
        return (
          <ToolCallGroup
            key={item.key}
            entries={item.entries}
            variant="inline"
          />
        );
      }

      const content =
        item.event.thinking?.content ?? item.event.assistantMessage?.content;
      if (content) {
        return (
          <div key={item.key} className={styles.commentaryCompleted}>
            <MarkdownContent>{content}</MarkdownContent>
          </div>
        );
      }

      return null;
    })}
  </div>
);

export const ThoughtProcess = ({
  items,
  isActive,
  isInterrupted,
  defaultExpanded = false,
}: ThoughtProcessProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const panelId = useId();

  if (isActive) {
    return <ActiveThoughtProcess items={items} isInterrupted={isInterrupted} />;
  }

  const headerLabel = isInterrupted
    ? 'Thought process (interrupted)'
    : 'Thought process';

  return (
    <div>
      <button
        type="button"
        className={styles.header}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => {
          setExpanded(prev => !prev);
        }}
      >
        <Text variant="body2" color="textSecondary">
          {headerLabel}
        </Text>
        <RiArrowRightSLine
          size={14}
          className={classNames(styles.arrow, {
            [styles.arrowExpanded]: expanded,
          })}
        />
      </button>
      <Collapse in={expanded}>
        <div id={panelId}>
          <ThoughtProcessContent items={items} />
        </div>
      </Collapse>
    </div>
  );
};
